export const INVOICE_COMPANY = {
  legalName: 'Virtual Physician Scribes, LLC',
  addressLine1: 'PO Box 247',
  addressLine2: 'McAllen, TX 78505-0247 USA',
  email: 'billing@vpsolutions.com',
};

const PLAN_LABELS = {
  starter: 'Starter',
  professional: 'Professional',
  elite: 'Elite',
  individual: 'Individual',
  group: 'Group',
  enterprise: 'Enterprise',
};

export function formatPlanLabel(planKey) {
  if (!planKey) return 'Premium';
  return PLAN_LABELS[planKey] || String(planKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatReceiptNumber(payment) {
  const id = String(payment?.id || '').replace(/-/g, '');
  const tail = id.length >= 8 ? id.slice(0, 8).toUpperCase() : (id || '00000000').toUpperCase();
  return `VPS-${tail}`;
}

export function formatShortDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

export function formatBillingPeriod(period) {
  if (!period) return '—';
  if (period === 'annually') return 'Annual';
  if (period === 'monthly') return 'Monthly';
  return String(period).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatAmount(amount, currency = 'USD') {
  const value = parseFloat(amount);
  if (!Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: String(currency || 'USD').toUpperCase(),
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

export function parseMetadata(payment) {
  if (!payment?.metadata) return {};
  if (typeof payment.metadata === 'string') {
    try {
      return JSON.parse(payment.metadata);
    } catch {
      return {};
    }
  }
  return payment.metadata;
}

export function buildLineItemDescription(payment) {
  const plan = formatPlanLabel(payment.plan_key);
  const period = formatBillingPeriod(payment.billing_period);
  const seats = payment.seat_count > 1 ? ` · ${payment.seat_count} seats` : '';
  if (payment.description && payment.description !== 'Payment') {
    return payment.description;
  }
  return `${plan} subscription (${period})${seats}`;
}

export function resolveInvoiceNumber(payment) {
  const meta = parseMetadata(payment);
  if (meta.stripeInvoiceNumber) return String(meta.stripeInvoiceNumber);
  return formatReceiptNumber(payment);
}

export function buildInvoiceLineItems(payment) {
  const amount = parseFloat(payment?.amount);
  const qty = payment?.seat_count > 0 ? payment.seat_count : 1;
  const rate = Number.isFinite(amount) ? amount / qty : amount;

  return [
    {
      date: formatShortDate(payment?.created_at),
      activity: 'Subscription',
      description: buildLineItemDescription(payment),
      qty,
      rate: formatAmount(rate),
      amount: formatAmount(amount),
    },
  ];
}

export function computeDueDate(invoiceDate, isPaid) {
  if (isPaid) return formatShortDate(invoiceDate);
  const d = invoiceDate instanceof Date ? new Date(invoiceDate) : new Date(invoiceDate);
  d.setDate(d.getDate() + 15);
  return formatShortDate(d);
}
